import React, { useState, useEffect } from 'react';
import Chat from './Chat'; // Chat interface component
import { FiLogOut, FiUpload } from 'react-icons/fi';
import { BsToggleOn, BsToggleOff } from 'react-icons/bs';

const Dashboard = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [recentConversations, setRecentConversations] = useState([]);
  const [userInfo, setUserInfo] = useState({
    id: null,
    firstName: 'User',
    lastName: '',
    email: '',
    statsReq: false,
    summaryReq: false,
    linkReq: false,
    profileImage: '/default-profile.png',
  });

  const toggleTheme = () => setDarkMode(!darkMode);

  useEffect(() => {
    // Fetch user data from localStorage
    const authData = JSON.parse(localStorage.getItem('auth'));
    if (authData && authData.id) {
      setUserInfo((prev) => ({
        ...prev,
        id: authData.id,
        firstName: authData.firstName || prev.firstName,
        lastName: authData.lastName || '',
        email: authData.email || '',
        statsReq: authData.statsReq || false,
        summaryReq: authData.summaryReq || false,
        linkReq: authData.linkReq || false,
        profileImage: authData.profileImage || '/default-profile.png',
      }));

      // Fetch recent conversations
      fetchRecentConversations(authData.id);
    } else {
      console.error('Auth data not found in localStorage. Redirecting to login...');
      window.location.href = '/'; // Redirect to login if auth data is missing
    }
  }, []);

  const fetchRecentConversations = async (userId) => {
    try {
      const response = await fetch(
        `https://x8ki-letl-twmt.n7.xano.io/api:ANgpKYYs/conversationsUser?id=${userId}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const sortedConversations = data
          .sort((a, b) => b.created_at - a.created_at)
          .slice(0, 5);
        setRecentConversations(sortedConversations);
      } else {
        console.error('Failed to fetch conversations:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const updateToggle = async (toggleName) => {
    try {
      const updatedValue = !userInfo[toggleName];
      const updatedUserInfo = { ...userInfo, [toggleName]: updatedValue };

      // Update backend
      const response = await fetch(
        'https://x8ki-letl-twmt.n7.xano.io/api:KeClO-2A/updateUserFlags',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: userInfo.id,
            [toggleName]: updatedValue,
          }),
        }
      );

      if (response.ok) {
        setUserInfo(updatedUserInfo);
      } else {
        console.error('Failed to update toggle:', response.statusText);
      }
    } catch (error) {
      console.error('Error updating toggle:', error);
    }
  };

  return (
    <div className={darkMode ? 'dark' : ''}>
      <div className="flex">
        {/* Sidebar */}
        <div className={`w-64 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-200 flex flex-col h-screen shadow-md`}>
          {/* Logo Section */}
          <div className="p-4 flex flex-col items-center">
            <img src="/logo.png" alt="InsightCore Logo" className="h-12 w-auto mb-2 object-contain" />
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Welcome to InsightCore, <span className="font-semibold">{userInfo.firstName}</span>
            </p>
          </div>

          {/* Chat Options */}
          <div className="p-4 space-y-4">
            {['statsReq', 'summaryReq', 'linkReq'].map((option, index) => (
              <div className="flex items-center justify-between" key={index}>
                <span className="text-sm capitalize">{option.replace('Req', '')}</span>
                {userInfo[option] ? (
                  <BsToggleOn
                    className="text-blue-500 cursor-pointer text-xl"
                    onClick={() => updateToggle(option)}
                  />
                ) : (
                  <BsToggleOff
                    className="text-gray-500 cursor-pointer text-xl"
                    onClick={() => updateToggle(option)}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Upload Button */}
          <div className="p-4">
            <button
              className="flex items-center justify-center w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-sm font-medium rounded shadow hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              <FiUpload className="mr-2" />
              Upload Internal Database
            </button>
          </div>

          {/* Divider */}
          <hr className="my-4 border-gray-300 dark:border-gray-700" />

          {/* Recent Conversations */}
          <div className="p-4">
            <p className="text-sm font-semibold mb-2">Recent Conversations</p>
            {recentConversations.length > 0 ? (
              <ul className="space-y-2">
                {recentConversations.map((conversation) => (
                  <li
                    key={conversation.id}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded shadow text-sm hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer overflow-hidden text-ellipsis"
                    title={conversation.convoTitle}
                  >
                    {conversation.convoTitle}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">No recent conversations.</p>
            )}
          </div>

          {/* Dark Mode Toggle */}
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm">Dark Mode</span>
            {darkMode ? (
              <BsToggleOn className="text-blue-500 cursor-pointer text-xl" onClick={toggleTheme} />
            ) : (
              <BsToggleOff className="text-gray-500 cursor-pointer text-xl" onClick={toggleTheme} />
            )}
          </div>

          {/* User Profile and Logout */}
          <div className="p-4 mt-auto flex items-center space-x-4">
            <img
              src={userInfo.profileImage}
              alt="User Profile"
              className="h-10 w-10 rounded-full border border-gray-300 dark:border-gray-600 object-cover"
            />
            <div className="flex-1">
              <p className="text-sm font-medium">{userInfo.firstName}</p>
            </div>
            <button
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              onClick={() => {
                localStorage.removeItem('auth');
                window.location.href = '/';
              }}
            >
              <FiLogOut className="text-lg" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4">
          <Chat />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
